import { useMemo } from 'react'
import { Trophy, CheckCircle2, Circle, Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import BracketTreeView, { ParticipantName } from './BracketTreeView'
import SwissBracket from './SwissBracket'
import GroupKOBracket from './GroupKOBracket'
import FFABracket from './FFABracket'
import LadderBracket from './LadderBracket'
import ConsolationBracket from './ConsolationBracket'

/* Top-level bracket-dispatcher — kiest per type de juiste view.
   Single-elim + double-elim vallen terug op de BracketTreeView (die de
   tokens en null-guards al gedeeld heeft; de oude grid-view is uit). */
export default function TournamentBracket({ matches, onMatchClick, isAdmin, tournamentType, tournamentId, participants }) {
  const participantMap = useMemo(() => {
    const m = {}
    ;(participants || []).forEach(p => { m[p.id] = p.team_name || p.username || `#${p.id}` })
    ;(matches || []).forEach(row => {
      if (row.participant1_id && !m[row.participant1_id] && row.participant1_name) m[row.participant1_id] = row.participant1_name
      if (row.participant2_id && !m[row.participant2_id] && row.participant2_name) m[row.participant2_id] = row.participant2_name
    })
    return m
  }, [participants, matches])

  // Naast de string-map ook een href-map: id → /teams/:id of /spelers/:id.
  // Bracket-componenten die dit ondersteunen wrappen de naam dan in een Link
  // zodat een klik op de speler-/team-naam het profiel opent (nieuw tabblad).
  const participantHrefs = useMemo(() => {
    const h = {}
    ;(participants || []).forEach(p => {
      if (!p.id) return
      h[p.id] = p.team_id ? `/teams/${p.team_id}` : (p.user_id ? `/spelers/${p.user_id}` : null)
    })
    ;(matches || []).forEach(row => {
      if (row.participant1_id && h[row.participant1_id] == null && row.participant1_team_id) {
        h[row.participant1_id] = `/teams/${row.participant1_team_id}`
      }
      if (row.participant2_id && h[row.participant2_id] == null && row.participant2_team_id) {
        h[row.participant2_id] = `/teams/${row.participant2_team_id}`
      }
    })
    return h
  }, [participants, matches])

  if (!matches || matches.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Trophy className="h-16 w-16 mx-auto mb-4 opacity-50" />
        <p>Nog geen bracket. Start het toernooi om er een te genereren.</p>
      </div>
    )
  }

  switch (tournamentType) {
    case 'swiss':
      return <SwissBracket tournamentId={tournamentId} matches={matches}
                           participantHrefs={participantHrefs}
                           onMatchClick={onMatchClick} isAdmin={isAdmin} />
    case 'group_ko':
      return <GroupKOBracket tournamentId={tournamentId} matches={matches}
                             participantMap={participantMap}
                             participantHrefs={participantHrefs}
                             onMatchClick={onMatchClick} isAdmin={isAdmin} />
    case 'ffa':
      return <FFABracket matches={matches} participantMap={participantMap}
                         participantHrefs={participantHrefs}
                         onMatchClick={onMatchClick} isAdmin={isAdmin} />
    case 'ladder':
      return <LadderBracket tournamentId={tournamentId} matches={matches}
                            participantMap={participantMap}
                            participantHrefs={participantHrefs}
                            onMatchClick={onMatchClick} isAdmin={isAdmin} />
    case 'consolation':
      return <ConsolationBracket matches={matches}
                                 participantHrefs={participantHrefs}
                                 onMatchClick={onMatchClick} isAdmin={isAdmin} />
    case 'round_robin':
      return <RoundRobinBracket matches={matches} participantMap={participantMap}
                                participantHrefs={participantHrefs}
                                onMatchClick={onMatchClick} isAdmin={isAdmin} />
    case 'double_elimination':
      // Double-elim heeft winners/loser/grand_final bracket-types; toon ze
      // los onder elkaar met de tree-view per sectie.
      return <DoubleEliminationView matches={matches}
                                    onMatchClick={onMatchClick} isAdmin={isAdmin}
                                    participantHrefs={participantHrefs} />
    case 'single_elimination':
    default:
      return <BracketTreeView matches={matches} onMatchClick={onMatchClick} isAdmin={isAdmin}
                              participantHrefs={participantHrefs} />
  }
}

/* Round-robin: iedereen speelt tegen iedereen — grid van matches. */
function RoundRobinBracket({ matches, participantMap, participantHrefs, onMatchClick, isAdmin }) {
  return (
    <div className="grid gap-2 md:grid-cols-2">
      {matches.slice().sort((a, b) => a.match_number - b.match_number).map(m => (
        <RRMatch key={m.id} match={m} participantMap={participantMap} participantHrefs={participantHrefs}
                 onClick={() => isAdmin && m.participant1_id && m.participant2_id && onMatchClick && onMatchClick(m)}
                 clickable={isAdmin && m.participant1_id && m.participant2_id} />
      ))}
    </div>
  )
}

function RRMatch({ match, participantMap, participantHrefs = {}, onClick, clickable }) {
  const done = match.status === 'completed'
  const chip = done ? <CheckCircle2 className="h-3 w-3 text-live" />
              : match.status === 'in_progress' ? <Clock className="h-3 w-3 text-live" />
              : <Circle className="h-3 w-3 text-muted-foreground" />
  const p1 = participantMap[match.participant1_id] || '?'
  const p2 = participantMap[match.participant2_id] || '?'
  const w1 = !!match.winner_id && match.winner_id === match.participant1_id
  const w2 = !!match.winner_id && match.winner_id === match.participant2_id
  return (
    <div onClick={clickable ? onClick : undefined}
         className={cn('flex items-center gap-2 text-sm px-3 py-2 rounded border border-border/60',
                       clickable && 'cursor-pointer hover:border-acid')}>
      {chip}
      <ParticipantName name={p1} href={participantHrefs[match.participant1_id]}
                       className={cn('flex-1 truncate', w1 && 'font-semibold text-live')} />
      <span className="text-muted-foreground text-xs">vs</span>
      <ParticipantName name={p2} href={participantHrefs[match.participant2_id]}
                       className={cn('flex-1 truncate text-right', w2 && 'font-semibold text-live')} />
      {match.map && <span className="font-mono text-[10px] uppercase text-live" title="Map (via veto)">{match.map}</span>}
      {match.score && <span className="font-mono tabular-nums text-muted-foreground w-16 text-right">{match.score}</span>}
    </div>
  )
}

/* Double-elimination: toont winners/losers/grand_final elk als aparte tree.
   Elk kwadrant erft de tokens uit BracketTreeView. */
function DoubleEliminationView({ matches, onMatchClick, isAdmin, participantHrefs }) {
  const winners = matches.filter(m => m.bracket_type === 'winner')
  const losers = matches.filter(m => m.bracket_type === 'loser')
  const grand = matches.filter(m => m.bracket_type === 'grand_final')
  return (
    <div className="space-y-8">
      {winners.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-acid">Winners bracket</CardTitle>
          </CardHeader>
          <CardContent>
            <BracketTreeView matches={winners} onMatchClick={onMatchClick} isAdmin={isAdmin}
                             participantHrefs={participantHrefs} />
          </CardContent>
        </Card>
      )}
      {losers.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-peach">Losers bracket</CardTitle>
          </CardHeader>
          <CardContent>
            <BracketTreeView matches={losers} onMatchClick={onMatchClick} isAdmin={isAdmin} tone="peach"
                             participantHrefs={participantHrefs} />
          </CardContent>
        </Card>
      )}
      {grand.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-gold">Grand final</CardTitle>
          </CardHeader>
          <CardContent>
            <BracketTreeView matches={grand} onMatchClick={onMatchClick} isAdmin={isAdmin}
                             participantHrefs={participantHrefs} />
          </CardContent>
        </Card>
      )}
    </div>
  )
}
